import { emitError } from '@conduit-client/bindings-utils/v1';
import {
    assertIsValid,
    MissingRequiredPropertyError,
    type JSONSchema,
    JsonSchemaViolationError,
} from '@conduit-client/jsonschema-validate';
import {
    type WireAdapter,
    type WireConfigValue,
    type WireContextValue,
    type WireDataCallback,
} from '@conduit-client/lwc-types';

import {
    type SyncOrAsync,
    type Unsubscribe,
    type SubscribableResult,
    type CommandError,
    deepFreeze,
    type Result,
    ok,
    isSubscribableResult,
    logError,
    isUserVisibleError,
} from '@conduit-client/utils';
import { type GraphQLResponse } from '@conduit-client/graphql-normalization/v1';
import sanitize from './sanitize';
import type {
    SubscribableResultCommand,
    ResultCommand,
    MaybeSubscribableResultCommand,
} from '@conduit-client/bindings-utils/v1';

export type WireAdapterCompatibleCommand<Data, Error> =
    | SubscribableResultCommand<Data, Error>
    | ResultCommand<Data, Error>
    | MaybeSubscribableResultCommand<Data, Error>;

export function isIncompleteConfigError(err: unknown): boolean {
    return (
        err instanceof MissingRequiredPropertyError ||
        (err instanceof JsonSchemaViolationError &&
            err.validationErrors.find(
                (validationError) => validationError instanceof MissingRequiredPropertyError
            ) !== undefined)
    );
}

export type GetCommand<
    Data,
    Config extends WireConfigValue = WireConfigValue,
    CommandType extends WireAdapterCompatibleCommand<
        Data,
        CommandError
    > = WireAdapterCompatibleCommand<Data, CommandError>,
> = (condig: Config) => CommandType;

/**
 * Binds a Command to a LWC wire adapter.
 * @see https://lwc.dev/guide/wire_adapter
 */
export abstract class CommandWireAdapterConstructor<
    Data,
    Config extends WireConfigValue = WireConfigValue,
    CommandType extends WireAdapterCompatibleCommand<
        Data,
        CommandError
    > = WireAdapterCompatibleCommand<Data, CommandError>,
> implements WireAdapter
{
    connected = false;
    config?: Config;
    unsubscriber?: Unsubscribe;
    exposeRefresh = false;

    abstract configSchema?: JSONSchema;
    abstract getCommand(): CommandType;
    protected refresh?: () => SyncOrAsync<Result<void, unknown>>;

    constructor(
        protected callback: WireDataCallback,
        sourceContext?: {
            tagName?: string;
        },
        options?: {
            skipEmptyEmit?: true;
        }
    ) {
        if (!options?.skipEmptyEmit) {
            this.emit();
        }
    }

    connect(): void {
        this.connected = true;
        this.invokeAdapter();
    }

    disconnect(): void {
        this.unsubscribe();
        this.connected = false;
    }

    update(config: Config, _context?: WireContextValue): void {
        this.unsubscribe();
        this.config = sanitize<Config>(config);
        this.invokeAdapter();
    }

    protected emit(result?: Result<Data, unknown> | SubscribableResult<Data, unknown>) {
        try {
            if (result === undefined) {
                this.callback({ data: undefined, error: undefined });
            } else {
                const consumerEmittedRefresh = (): Promise<void> => {
                    if (!this.refresh) {
                        return Promise.resolve();
                    }
                    return new Promise((resolve, reject) => {
                        if (!this.refresh) {
                            resolve();
                            return;
                        }
                        this.refresh().then((res) => {
                            if (res.isOk()) {
                                resolve();
                            } else {
                                reject(
                                    new Error(
                                        'Internal error in Lightning Data Service adapter occurred: Failed to refresh data'
                                    )
                                );
                            }
                        });
                    });
                };

                let consumerEmittedData = {
                    data: undefined,
                    error: undefined,
                } as {
                    data: Data | undefined;
                    error: unknown | undefined;
                    refresh?: () => Promise<void>;
                };

                if (this.exposeRefresh && this.refresh) {
                    consumerEmittedData.refresh = consumerEmittedRefresh;
                }

                if (result.isErr()) {
                    if (isSubscribableResult(result)) {
                        consumerEmittedData.error = result.error.failure;
                    } else {
                        consumerEmittedData.error = result.error;
                    }
                } else {
                    if (isSubscribableResult(result)) {
                        deepFreeze(result.value.data);
                        consumerEmittedData.data = result.value.data;
                    } else {
                        deepFreeze(result.value);
                        consumerEmittedData.data = result.value;
                    }
                }

                this.callback(consumerEmittedData);
            }
        } catch (e) {
            this.handleExecutionThrow(e);
        }
    }

    protected invokeAdapter() {
        if (!this.connected || this.config === undefined) {
            return;
        }

        if (this.configSchema) {
            try {
                assertIsValid<unknown>(this.config, this.configSchema);
            } catch (err) {
                if (isIncompleteConfigError(err)) {
                    // config is incomplete, wait for updated config
                    return;
                }

                if (err instanceof JsonSchemaViolationError) {
                    this.handleConfigSchemaViolation(err);
                    return;
                }

                throw err;
            }
        }

        const initialConfig = this.config;
        const command = this.getCommand();

        try {
            command.execute().then((result) => {
                // config changed or component disconnected before result came back, ignore result
                if (!this.connected || this.config !== initialConfig) {
                    return;
                }

                this.refresh = undefined;

                if (result.isOk()) {
                    if (isSubscribableResult<Data, unknown>(result)) {
                        const value = result.value;
                        this.unsubscriber = value.subscribe((updatedResult) => {
                            if (!this.connected || this.config !== initialConfig) {
                                this.unsubscribe();
                                return;
                            }

                            this.emit(updatedResult);
                        });
                        this.refresh = value.refresh;
                        this.emit(ok(value.data));
                    } else {
                        this.emit(result);
                    }
                } else {
                    if (isSubscribableResult(result)) {
                        const value = result.error;
                        this.unsubscriber = value.subscribe((updatedResult) => {
                            if (!this.connected || this.config !== initialConfig) {
                                this.unsubscribe();
                                return;
                            }

                            this.emit(updatedResult);
                        });
                        this.refresh = value.refresh;
                        this.emit(result);
                    } else {
                        this.unsubscriber = () => {};
                        this.emit(result);
                    }
                }
            });
        } catch (e: unknown) {
            this.handleExecutionThrow(e);
        }
    }

    protected handleExecutionThrow(error: unknown) {
        emitError(this.callback, error);
    }

    /**
     * Hook for subclasses to handle a JsonSchemaViolationError from the config
     * schema assertion. The default re-throws so legacy non-GraphQL wire
     * adapters keep their existing throw behavior. Overrides are expected to
     * either emit an error result or re-throw — silently returning will
     * swallow the error.
     */
    protected handleConfigSchemaViolation(error: JsonSchemaViolationError): void {
        throw error;
    }

    protected unsubscribe() {
        if (this.unsubscriber) {
            this.unsubscriber();
            delete this.unsubscriber;
        }
    }
}

/**
 * Maps a command failure (unknown or user-visible) into a GraphQLResponse suitable for consumers.
 */
export function toGraphQLResponseFromFailure(failure: unknown): {
    data: GraphQLResponse['data'];
    errors: GraphQLResponse['errors'];
} {
    if (isUserVisibleError<GraphQLResponse>(failure)) {
        return {
            data: failure.data.data,
            errors: failure.data.errors,
        };
    }
    logError(failure);
    return {
        data: undefined,
        errors: [{ message: 'Internal error in GraphQL adapter occurred', locations: [] }],
    };
}
